import { Injectable, NgZone } from '@angular/core';
import OLMap from 'ol/Map';
import { EventsKey } from 'ol/events';
import { unByKey } from 'ol/Observable';
import VectorLayer from 'ol/layer/Vector';
import BaseLayer from 'ol/layer/Base';

interface MapAnimation {
    layers: Map<VectorLayer, EventsKey[]>;
    listeners: EventsKey[];
    visibilityChanged: () => void;
    frame: number | null;
    time: number;
}

@Injectable({
    providedIn: 'root'
})
export class ArcGISAnimationService {
    private readonly maps = new WeakMap<OLMap, MapAnimation>();

    constructor(private ngZone: NgZone) {}

    public getTime(map: OLMap): number {
        return this.maps.get(map)?.time ?? 0;
    }

    public register(map: OLMap, layer: VectorLayer): void {
        this.ngZone.runOutsideAngular(() => {
            let state = this.maps.get(map);
            if (!state) {
                const visibilityChanged = (): void => this.schedule(map);
                state = { layers: new Map(), listeners: [], frame: null, time: performance.now() / 1000, visibilityChanged };
                this.maps.set(map, state);
                state.listeners.push(
                    map.getLayers().on('add', visibilityChanged),
                    map.getLayers().on('remove', visibilityChanged),
                    map.getView().on('change:resolution', visibilityChanged),
                    map.on('change:target', visibilityChanged),
                    map.on('change:size', visibilityChanged)
                );
                document.addEventListener('visibilitychange', visibilityChanged);
            }
            if (!state.layers.has(layer)) {
                const changed = (): void => this.schedule(map);
                const listeners = [
                    layer.on('change:visible', changed),
                    layer.on('change:opacity', changed),
                    layer.on('change:minResolution', changed),
                    layer.on('change:maxResolution', changed),
                    layer.on('change:minZoom', changed),
                    layer.on('change:maxZoom', changed),
                    layer.on('change:source', () => {
                        this.unregister(map, layer);
                        if (layer.getSource()) this.register(map, layer);
                    })
                ];
                const source = layer.getSource();
                if (source) listeners.push(source.on('change', changed));
                state.layers.set(layer, listeners);
            }
            this.schedule(map);
        });
    }

    public unregister(map: OLMap, layer: BaseLayer): void {
        if (!(layer instanceof VectorLayer)) return;
        const state = this.maps.get(map);
        const listeners = state?.layers.get(layer);
        if (!state || !listeners) return;
        unByKey(listeners);
        state.layers.delete(layer);
        if (!state.layers.size) this.dispose(map);
        else this.schedule(map);
    }

    public dispose(map: OLMap): void {
        const state = this.maps.get(map);
        if (!state) return;
        if (state.frame !== null) cancelAnimationFrame(state.frame);
        state.layers.forEach(listeners => unByKey(listeners));
        unByKey(state.listeners);
        document.removeEventListener('visibilitychange', state.visibilityChanged);
        this.maps.delete(map);
    }

    private activeLayers(map: OLMap, state: MapAnimation): VectorLayer[] {
        const size = map.getSize();
        if (document.hidden || !map.getTargetElement() || !size || size[0] <= 0 || size[1] <= 0) return [];
        const present = map.getLayers().getArray();
        const resolution = map.getView().getResolution();
        const zoom = map.getView().getZoom();
        return Array.from(state.layers.keys()).filter(layer =>
            present.includes(layer) && layer.getVisible() && layer.getOpacity() > 0 && !!layer.getSource() && !layer.getSource()?.isEmpty() &&
            (resolution === undefined || (resolution >= layer.getMinResolution() && resolution < layer.getMaxResolution())) &&
            (zoom === undefined || (zoom > layer.getMinZoom() && zoom <= layer.getMaxZoom()))
        );
    }

    private schedule(map: OLMap): void {
        const state = this.maps.get(map);
        if (!state) return;
        if (!this.activeLayers(map, state).length) {
            if (state.frame !== null) cancelAnimationFrame(state.frame);
            state.frame = null;
            return;
        }
        if (state.frame !== null) return;
        this.ngZone.runOutsideAngular(() => {
            state.frame = requestAnimationFrame(time => {
                state.time = time / 1000;
                for (const layer of this.activeLayers(map, state)) layer.changed();
                state.frame = null;
                this.schedule(map);
            });
        });
    }
}
